// Écran 7 — Écritures dans les outils de l'entreprise (GLPI, CISO Assistant, Mercator), en deux temps (O95). (1) La
// personne connectée envoie sa demande ; l'API fait calculer le plan par le collecteur de l'entreprise (qui relit
// l'outil), le garde pour elle et le rend avec son empreinte et son échéance : rien n'est signé. (2) Elle lit ce
// plan, puis le valide après une confirmation explicite : l'API ne signe que ce plan gardé. L'écran remet ensuite
// plan et validation tels quels au connecteur. Constats O3, O39 et O95 : ni l'application, ni un script, ni un agent
// ne détient la clé qui signe ; plan et validation ne vivent qu'en mémoire, le temps de la remettre.
// O100 : la validation exige en plus un geste physique (WebAuthn) de la personne, après la lecture du plan et la
// confirmation : défi lié au plan, geste, puis envoi ; défi et assertion ne sont jamais gardés (un essai par défi).
import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useState, type ChangeEvent } from 'react';
import { ErreurApi, TEXTE_APPLICATIONS_HUMAINES_NON_DECLAREES, TEXTE_APPLICATION_NON_INTERACTIVE } from '../api/client';
import { useEkera } from '../contexte';
import { dateFr } from '../domaine/format';
import { ErreurGeste, gesteAssertion, indisponibiliteNavigateur } from '../domaine/webauthn';
import { lienVers } from '../routeur';
import { telecharger } from '../domaine/telechargement';
import {
  dureeLisible,
  ecartsPlanGarde,
  memePlan,
  ecartsValidation,
  empreintePlan,
  LIBELLE_ACTION,
  LIBELLE_LOGICIEL,
  lireDemande,
  planValide,
  resumePlan,
  tempsRestant,
  type DemandeLue,
  type PlanEcriture,
  type ReponsePlan,
  type Validation,
} from '../domaine/validations';
import { Erreur } from '../composants/communs';

interface DemandeChargee {
  demande: DemandeLue;
  fichier: string;
  numero: number;
}

export function EcranOutils() {
  const [lu, setLu] = useState<DemandeChargee | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const idFichier = useId();

  async function charger(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    setErreur(null);
    setLu(null);
    if (!f) return;
    try {
      if (f.size > 5_000_000) throw new Error('fichier trop gros pour un plan (5 Mo au plus)');
      const demande = await lireDemande(await f.text());
      setLu({ demande, fichier: f.name, numero: Date.now() });
    } catch (x) {
      setErreur(x instanceof Error ? x.message : String(x));
    }
  }

  return (
    <div className="pile">
      <section className="panneau" aria-labelledby="t-principe">
        <h2 id="t-principe">Ekera n’écrit dans vos outils qu’avec votre validation</h2>
        <ol className="liste-etapes">
          <li>Vous décrivez ce que vous voulez écrire (la <b>demande</b>). Vous n’envoyez aucun plan.</li>
          <li>L’API d’Ekera fait calculer le <b>plan exact</b> par le collecteur de votre entreprise, qui relit l’outil : chaque action, champ par champ. Elle le garde pour vous, quelques minutes, et ne signe encore rien. Ce plan peut différer de ce que vous attendiez.</li>
          <li>Vous lisez le plan, puis vous le <b>validez après une confirmation explicite</b> : l’API émet alors <b>à votre nom</b> une validation signée, liée à ce plan précis, qui sert <b>une seule fois</b>.</li>
          <li>Vous remettez le plan validé au connecteur ; il reste dans cet onglet le temps de la remettre. Rien n’est écrit tant que vous ne l’avez pas remis.</li>
          <li>Le connecteur la vérifie (signature, plan identique, échéance, usage unique), relit l’outil et refuse d’écrire s’il a changé.</li>
        </ol>
        <p className="note">
          Ni cette application, ni un agent, ni un script ne détient la clé qui signe : seule l’API la détient, et elle ne signe que pour une
          personne connectée qui a le rôle d’écriture. Rien n’est jamais supprimé dans vos outils.
        </p>
      </section>

      <section className="panneau" aria-labelledby="t-plan">
        <h2 id="t-plan">Demande d’écriture</h2>
        <div className="champ">
          <label htmlFor={idFichier}>
            Fichier de la demande (JSON : logiciel, table et demande)
            <span className="aide"> — par exemple « demande-ticket.json » ; les actions d’un ancien fichier de plan ne sont pas envoyées</span>
          </label>
          <input id={idFichier} type="file" accept="application/json,.json" onChange={(e) => void charger(e)} data-testid="fichier-plan" />
        </div>
        {erreur && (
          <p className="encadre erreur" role="alert">
            Demande refusée : {erreur}
          </p>
        )}
      </section>

      {lu && <CircuitValidation key={lu.numero} {...lu} />}
    </div>
  );
}

type Etat =
  | { etape: 'lecture' }
  | { etape: 'demande' }
  | { etape: 'plan'; recu: ReponsePlan; empreinte: string; confirmation: boolean; envoi: false | 'defi' | 'geste' | 'signature' }
  | { etape: 'validee'; plan: PlanEcriture; empreinte: string; validation: Validation; attestation: Record<string, unknown> | null }
  | { etape: 'remise'; fichier: string }
  | { etape: 'expiree'; de: 'plan' | 'validation' }
  | { etape: 'abandon' };

export function CircuitValidation({ demande, fichier }: DemandeChargee) {
  const { client, cid, moi } = useEkera();
  const [etat, setEtat] = useState<Etat>({ etape: 'lecture' });
  const [commentaire, setCommentaire] = useState('');
  // Circuit de validation (CV-2, CV-3) : l'attestation de fin de circuit choisie pour exécuter ce plan, ou rien
  const [attestation, setAttestation] = useState<string | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [maintenant, setMaintenant] = useState(() => new Date());
  const idCommentaire = useId();

  // Compte à rebours ; à l'échéance du plan gardé, ou de la validation, ils sont oubliés (ils ne serviraient plus à rien).
  useEffect(() => {
    if (etat.etape !== 'plan' && etat.etape !== 'validee') return;
    const t = window.setInterval(() => {
      const n = new Date();
      setMaintenant(n);
      if (etat.etape === 'plan' && !etat.envoi && tempsRestant({ expire: etat.recu.expire }, n) === 0) {
        setErreur(null);
        setEtat({ etape: 'expiree', de: 'plan' });
      }
      if (etat.etape === 'validee' && tempsRestant(etat.validation, n) === 0) setEtat({ etape: 'expiree', de: 'validation' });
    }, 1000);
    return () => window.clearInterval(t);
  }, [etat]);

  /** Premier temps : demander le plan. L'API le calcule, le garde et rend son empreinte ; rien n'est signé. */
  async function demanderPlan() {
    setErreur(null);
    setEtat({ etape: 'demande' });
    try {
      const recu = await client.demanderPlan(cid, demande);
      // contrôle côté navigateur : l'empreinte recalculée sur le plan reçu doit être celle que l'API annonce
      const empreinte = await empreintePlan(recu.plan);
      setMaintenant(new Date());
      setEtat({ etape: 'plan', recu, empreinte, confirmation: false, envoi: false });
    } catch (e) {
      setErreur(e);
      setEtat({ etape: 'lecture' });
    }
  }

  /** Second temps, après la confirmation : l'API signe le plan gardé désigné par plan_id et l'empreinte vue. */
  async function valider(courant: Extract<Etat, { etape: 'plan' }>) {
    setErreur(null);
    setEtat({ ...courant, envoi: 'defi' });
    try {
      // O100 : défi lié au plan gardé, puis geste de la personne sur ce défi (un seul essai par défi) ; ni le défi ni
      // l'assertion ne sont gardés ailleurs que dans ces variables locales.
      const defi = await client.defiPlan(cid, courant.recu.plan_id);
      setEtat({ ...courant, envoi: 'geste' });
      const assertion = await gesteAssertion(defi.options);
      setEtat({ ...courant, envoi: 'signature' });
      // l'empreinte envoyée est celle recalculée ici sur le plan lu : l'API la compare à celle du plan qu'elle garde
      const r = await client.valider(
        cid,
        { plan_id: courant.recu.plan_id, empreinte: courant.empreinte, defi_id: defi.defi_id, assertion, attestation_id: attestation },
        commentaire,
      );
      // le plan rendu doit être celui qui a été affiché ; il est remis tel quel, sans être modifié
      const empreinte = await empreintePlan(r.plan);
      const ecarts = memePlan(r.plan, courant.recu.plan) ? [] : ['l’API a rendu un autre plan que celui que vous avez lu'];
      setMaintenant(new Date());
      if (ecarts.length) {
        setErreur(new Error(`${ecarts.join(' ; ')}. Rien ne sera remis.`));
        setEtat({ etape: 'lecture' });
        return;
      }
      setEtat({ etape: 'validee', plan: r.plan, empreinte, validation: r.validation, attestation: (r.attestation as Record<string, unknown> | null | undefined) ?? null });
    } catch (e) {
      setErreur(e);
      // geste raté ou défi perdu : rien n'est signé et le plan reste validable, on redemande un défi au prochain clic
      setEtat(resteValidable(e) ? { ...courant, envoi: false } : { etape: 'lecture' });
    }
  }

  function remettre(plan: PlanEcriture, v: Validation, att: Record<string, unknown> | null) {
    const f = planValide(plan, v, att);
    telecharger(new Blob([f.contenu], { type: 'application/json' }), f.nom);
    // remise faite : la validation n'est plus gardée ici (ni en mémoire, ni ailleurs)
    setEtat({ etape: 'remise', fichier: f.nom });
  }

  const logiciel = LIBELLE_LOGICIEL[demande.logiciel] ?? demande.logiciel;
  const garde = etat.etape === 'plan' ? etat : null;
  const recu = etat.etape === 'validee' ? etat : null;
  const planAffiche = garde ? garde.recu.plan : recu ? recu.plan : null;
  const empreinteAffichee = garde ? garde.empreinte : recu ? recu.empreinte : null;
  const ecartsGarde = garde ? ecartsPlanGarde({ ...garde.recu }, garde.empreinte, demande) : [];
  const ecarts = recu
    ? [...ecartsValidation(recu.validation, { empreinte: recu.empreinte }, cid, moi.sujet), ...ecartsPlanGarde({ plan: recu.plan, empreinte: recu.empreinte, expire: recu.validation.expire ?? '' }, recu.empreinte, demande)]
    : [];
  const attendu =
    planAffiche && empreinteAffichee && demande.empreinteAttendue !== null ? demande.empreinteAttendue === empreinteAffichee : null;
  const peutDemander = etat.etape === 'lecture' || etat.etape === 'demande' || etat.etape === 'expiree';
  return (
    <section className="panneau pile apparait" aria-labelledby="t-circuit" data-testid="circuit-validation">
      <div className="panneau-tete">
        <h2 id="t-circuit">
          {logiciel} : {demande.demande.nature} <span className="num">{demande.demande.cle}</span>
        </h2>
        <span className="indice">Fichier {fichier} · rien n’est encore écrit</span>
      </div>
      <dl className="paires">
        <dt>Outil</dt>
        <dd>
          {logiciel} (table de correspondance <span className="num">{demande.table}</span>)
        </dd>
        {demande.demande.cible && (
          <>
            <dt>Élément visé</dt>
            <dd className="num">{demande.demande.cible}</dd>
          </>
        )}
      </dl>
      {(etat.etape === 'lecture' || etat.etape === 'demande') && (
        <p className="note">
          Le plan exact n’existe pas encore : il sera calculé par le collecteur de votre entreprise quand vous le demanderez, et vous le lirez
          avant de valider quoi que ce soit.
        </p>
      )}
      {planAffiche && empreinteAffichee && (
        <PlanRecalcule plan={planAffiche} empreinte={empreinteAffichee} logiciel={logiciel} attendu={attendu} />
      )}

      {peutDemander &&
        (moi.validation_ecritures ? (
          <div className="pile" style={{ gap: 8 }}>
            {etat.etape === 'expiree' && (
              <p className="encadre attention" role="alert" data-testid="expire">
                {etat.de === 'plan'
                  ? 'Le plan a expiré avant d’être validé : l’API ne le garde plus et rien n’a été validé. Redemandez-le, puis relisez-le.'
                  : 'La validation a expiré sans être utilisée : elle est oubliée. Redemandez le plan si besoin.'}
              </p>
            )}
            {!!erreur && <Erreur erreur={erreur} titre="Le plan n’a pas été obtenu, aucune validation n’a été émise." />}
            {!!erreur && versMesCles(erreur) && <LienMesCles />}
            <div className="ligne">
              <button type="button" className="btn principal" disabled={etat.etape === 'demande'} onClick={() => void demanderPlan()}>
                {etat.etape === 'demande'
                  ? 'Calcul du plan par le collecteur…'
                  : etat.etape === 'expiree'
                    ? 'Redemander le plan'
                    : 'Demander le plan'}
              </button>
            </div>
          </div>
        ) : (
          <p className="encadre info" data-testid="validation-impossible">
            {pourquoiPasDeValidation(moi)}{' '}
            {(moi.validation_ecritures_motif === 'cle_non_enrolee' || moi.validation_ecritures_motif === 'cle_en_carence') && <LienMesCles />}
          </p>
        ))}

      {garde && !!erreur && (
        <div className="pile" style={{ gap: 6 }} data-testid="erreur-validation">
          <Erreur erreur={erreur} titre="La validation n’a pas été émise : rien n’est signé." />
          {versMesCles(erreur) && <LienMesCles />}
        </div>
      )}
      {garde && (
        <div className={`encadre ${ecartsGarde.length ? 'erreur' : 'info'} pile`} style={{ gap: 8 }} data-testid="plan-garde">
          <p>
            Plan gardé par l’API pour vous, jusqu’à {heure(garde.recu.expire)} · reste{' '}
            <span data-testid="reste-plan">{dureeLisible(tempsRestant({ expire: garde.recu.expire }, maintenant))}</span>. Rien n’est encore
            signé.
          </p>
          <p>
            Empreinte annoncée par l’API :{' '}
            <span className="num empreinte-entiere" data-testid="empreinte-api">
              {garde.recu.empreinte}
            </span>{' '}
            {garde.empreinte === garde.recu.empreinte ? '— identique à celle recalculée dans votre navigateur.' : '— DIFFÉRENTE de celle recalculée dans votre navigateur.'}
          </p>
          {ecartsGarde.length > 0 ? (
            <p role="alert">Plan refusé par l’application : {ecartsGarde.join(' ; ')}. Il ne sera pas validé.</p>
          ) : !garde.confirmation ? (
            <>
            <CircuitEcriture planId={garde.recu.plan_id} attestation={attestation} surAttestation={setAttestation} />
            <div className="ligne">
              <button type="button" className="btn principal" onClick={() => setEtat({ ...garde, confirmation: true })}>
                Valider ce plan
              </button>
              <button type="button" className="btn" onClick={() => setEtat({ etape: 'abandon' })}>
                Ne pas le valider
              </button>
            </div>
            </>
          ) : (
            <div className="pile" style={{ gap: 8 }} role="group" aria-labelledby="t-confirmation" data-testid="confirmation">
              <p id="t-confirmation">
                <b>
                  Vous allez valider à votre nom ({moi.nom ?? moi.sujet}) le plan d’empreinte{' '}
                  <span className="num empreinte-entiere">{garde.empreinte}</span> : {planAffiche ? phraseActions(planAffiche) : ''}.
                </b>{' '}
                La validation ne sert qu’une fois et vaut 15 minutes ; elle n’écrit encore rien dans {logiciel}.
              </p>
              <p className="note" data-testid="annonce-geste">
                Après votre confirmation, le navigateur vous demandera de <b>toucher votre clé de sécurité</b> (et de saisir son code ou de poser
                votre doigt) : ce geste ne vaut que pour ce plan, une seule fois. Sans lui, rien n’est signé.
              </p>
              {indisponibiliteNavigateur() && (
                <p className="encadre erreur" role="alert" data-testid="webauthn-absent">
                  {indisponibiliteNavigateur()}
                </p>
              )}
              {garde.envoi === 'geste' && (
                <p className="encadre attention" role="status" data-testid="attente-geste">
                  Touchez votre clé de sécurité pour valider ce plan (le navigateur attend 60 secondes au plus)…
                </p>
              )}
              <div className="champ">
                <label htmlFor={idCommentaire}>
                  Commentaire <span className="aide">(facultatif : motif, conditions ; gardé avec la validation)</span>
                </label>
                <textarea id={idCommentaire} maxLength={2000} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
              </div>
              <div className="ligne">
                <button
                  type="button"
                  className="btn principal"
                  disabled={!!garde.envoi || !!indisponibiliteNavigateur()}
                  onClick={() => void valider(garde)}
                >
                  {garde.envoi === 'defi'
                    ? 'Demande du défi à l’API…'
                    : garde.envoi === 'geste'
                      ? 'En attente de votre geste…'
                      : garde.envoi === 'signature'
                        ? 'Signature par l’API…'
                        : 'Confirmer : je valide ce plan'}
                </button>
                <button type="button" className="btn" disabled={!!garde.envoi} onClick={() => setEtat({ ...garde, confirmation: false })}>
                  Revenir au plan
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {etat.etape === 'validee' && (
        <div className={`encadre ${ecarts.length ? 'erreur' : 'ok'} pile`} style={{ gap: 6 }} data-testid="validation-recue">
          <p role="status">
            <b>
              Validation signée par l’API pour {etat.validation.par}
              {etat.validation.identite?.sujet && etat.validation.identite.sujet !== etat.validation.par
                ? ` (${etat.validation.identite.sujet})`
                : ''}
            </b>{' '}
            le {dateFr(etat.validation.le)}.
          </p>
          <p>
            Plan validé :{' '}
            <span className="num empreinte-entiere" data-testid="empreinte-validee">
              {etat.validation.empreinte_plan ?? ''}
            </span>{' '}
            {etat.validation.empreinte_plan === etat.empreinte ? '— identique au plan affiché.' : '— DIFFÉRENT du plan affiché.'}
          </p>
          <p>
            Usage unique (jeton <span className="num">{etat.validation.jeton}</span>) · valable 15 minutes, jusqu’à{' '}
            {heure(etat.validation.expire)} · reste <span data-testid="reste">{dureeLisible(tempsRestant(etat.validation, maintenant))}</span>
          </p>
          {ecarts.length > 0 ? (
            <p role="alert">Validation refusée par l’application : {ecarts.join(' ; ')}. Elle ne sera pas remise.</p>
          ) : (
            <div className="ligne">
              <button type="button" className="btn sombre" onClick={() => remettre(etat.plan, etat.validation, etat.attestation)}>
                Remettre au connecteur (plan validé)
              </button>
              <button type="button" className="btn" onClick={() => setEtat({ etape: 'abandon' })}>
                Ne pas l’utiliser
              </button>
            </div>
          )}
        </div>
      )}
      {etat.etape === 'remise' && (
        <p className="encadre ok" role="status" data-testid="validation-remise">
          Plan validé remis au connecteur (fichier <span className="num">{etat.fichier}</span>). La validation n’est plus gardée dans
          cette application ; le connecteur la vérifie avant d’écrire, et le journal de l’API garde qui l’a donnée.
        </p>
      )}
      {etat.etape === 'abandon' && (
        <p className="encadre info" role="status">
          Plan oublié par l’application : rien ne sera écrit avec lui (l’API ne le garde que quelques minutes, une validation éventuelle
          expire d’elle-même).{' '}
          <button type="button" className="lien-btn" onClick={() => setEtat({ etape: 'lecture' })}>
            Revoir la demande
          </button>
        </p>
      )}
    </section>
  );
}

function phraseActions(plan: PlanEcriture): string {
  const r = resumePlan(plan);
  return r.length ? r.map((x) => `${LIBELLE_ACTION[x.nature] ?? x.nature} : ${x.nombre}`).join(' · ') : 'aucune action';
}

function PlanRecalcule({ plan, empreinte, logiciel, attendu }: { plan: PlanEcriture; empreinte: string; logiciel: string; attendu: boolean | null }) {
  const resume = resumePlan(plan);
  return (
    <div className="pile" style={{ gap: 8 }} data-testid="plan-recalcule">
      <h3>Plan calculé par le collecteur de votre entreprise, gardé par l’API</h3>
      {attendu === false && (
        <p className="encadre attention" role="status" data-testid="plan-different">
          Ce plan diffère de celui du fichier que vous avez chargé : l’outil a changé depuis, le collecteur a relu son état. Lisez-le
          attentivement : c’est lui, et lui seul, que la validation couvre.
        </p>
      )}
      {attendu === true && <p className="note">Identique au plan du fichier chargé.</p>}
      <dl className="paires">
        <dt>Actions</dt>
        <dd>{resume.length ? resume.map((r) => `${LIBELLE_ACTION[r.nature] ?? r.nature} : ${r.nombre}`).join(' · ') : 'aucune'}</dd>
        <dt>Versions relues avant d’écrire</dt>
        <dd>{Object.keys(plan.versions_lues ?? {}).length}</dd>
        <dt>Empreinte du plan affiché</dt>
        <dd>
          <span className="num empreinte-entiere" data-testid="empreinte-locale">
            {empreinte}
          </span>{' '}
          <span className="note">(SHA-256 du plan en JSON canonique, calculée dans votre navigateur)</span>
        </dd>
      </dl>
      <div className="tableau-defile">
        <table>
          <caption>Le plan, action par action</caption>
          <thead>
            <tr>
              <th scope="col">Action</th>
              <th scope="col">Objet dans {logiciel}</th>
              <th scope="col">Motif</th>
              <th scope="col">Champs envoyés</th>
            </tr>
          </thead>
          <tbody>
            {plan.actions.map((a, i) => (
              <tr key={i}>
                <td>{LIBELLE_ACTION[a.nature] ?? a.nature}</td>
                <td>
                  <span className="num">
                    {a.type_logiciel}
                    {a.identifiant ? ` ${a.identifiant}` : ''}
                  </span>
                </td>
                <td>{a.motif}</td>
                <td>
                  <ChampsEnvoyes donnees={a.donnees ?? {}} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ChampsEnvoyes({ donnees }: { donnees: Record<string, unknown> }) {
  const champs = Object.entries(donnees);
  if (!champs.length) return <span className="note">aucun</span>;
  return (
    <details>
      <summary>
        {champs.length} champ{champs.length > 1 ? 's' : ''}
      </summary>
      <dl className="paires">
        {champs.map(([k, v]) => (
          <div key={k} style={{ display: 'contents' }}>
            <dt className="num">{k}</dt>
            <dd className="valeur-longue">{typeof v === 'string' ? v : JSON.stringify(v)}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

function heure(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleTimeString('fr-FR') : '—';
}

/** Après ces erreurs, rien n'est signé et le plan reste validable : un nouveau clic redemande un défi. */
const CODES_PLAN_VALIDABLE = new Set(['geste_refuse', 'defi_expire', 'defis_trop_nombreux', 'defis_session_trop_nombreux', 'defis_compte_trop_nombreux', 'gestes_refuses_trop_nombreux']);
export function resteValidable(e: unknown): boolean {
  if (e instanceof ErreurGeste) return true;
  return e instanceof ErreurApi && CODES_PLAN_VALIDABLE.has(e.code);
}

function versMesCles(e: unknown): boolean {
  return e instanceof ErreurApi && (e.code === 'cle_non_enrolee' || e.code === 'adresse_alerte_invalide');
}

function LienMesCles() {
  return (
    <a href={lienVers('cles')} data-testid="lien-mes-cles">
      Aller à « Mes clés »
    </a>
  );
}

/** Pourquoi l'API ne validerait pas pour cette personne (GET /v0/moi : validation_ecritures faux). */
export function pourquoiPasDeValidation(moi: {
  roles: readonly string[];
  compte_service?: boolean;
  anonyme?: boolean;
  validation_ecritures_motif?: string | null;
  webauthn?: { prochaine_activation?: string | null } | null;
}): string {
  if (moi.anonyme) return 'Appel anonyme (mode développement sans jeton) : une validation exige une personne connectée.';
  if (moi.compte_service) return 'Un compte de service (agent, outil) ne valide jamais une écriture : seule une personne le peut.';
  if (!moi.roles.includes('ecriture') && !moi.roles.includes('administration'))
    return 'Votre rôle ne permet pas de valider une écriture dans les outils (rôle « écriture » nécessaire). Vous pouvez lire le plan.';
  if (moi.validation_ecritures_motif === 'application_non_interactive') return TEXTE_APPLICATION_NON_INTERACTIVE;
  if (moi.validation_ecritures_motif === 'applications_humaines_non_declarees') return TEXTE_APPLICATIONS_HUMAINES_NON_DECLAREES;
  if (moi.validation_ecritures_motif === 'cle_non_enrolee')
    return 'Vous n’avez aucune clé de sécurité active : une écriture ne se valide que par un geste sur votre clé. Enregistrez-en une, puis faites-la approuver par un administrateur.';
  if (moi.validation_ecritures_motif === 'cle_en_carence') {
    const quand = moi.webauthn?.prochaine_activation ? ` le ${dateFr(moi.webauthn.prochaine_activation)}` : ' à la fin du délai de sécurité';
    return `Votre clé est approuvée, mais pas encore active : elle le sera${quand}. Jusque-là, aucune écriture ne peut être validée.`;
  }
  if (moi.validation_ecritures_motif === 'webauthn_indisponible')
    return 'La validation par clé de sécurité n’est pas configurée sur cette installation : aucune validation n’est possible, et rien ne permet de la contourner.';
  return 'La validation des écritures n’est pas configurée sur cette installation (clé de validation ou collecteur du site absent de l’API).';
}

/**
 * Le circuit de validation d'une écriture (CV-2, CV-3) : soumettre ce plan gardé au circuit du client, ou l'exécuter
 * avec l'attestation d'une instance approuvée (le plan recalculé doit avoir la même empreinte d'écriture, sinon l'API
 * refuse et une nouvelle instance est nécessaire).
 */
function CircuitEcriture({ planId, attestation, surAttestation }: { planId: string; attestation: string | null; surAttestation: (a: string | null) => void }) {
  const { client, cid } = useEkera();
  const hist = useQuery({ queryKey: ['circuits', 'historique'], queryFn: () => client.historiqueCircuit(), retry: false });
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const approuvees = (hist.data?.instances ?? []).filter((x) => x.type_objet === 'ecriture_outil' && x.etat === 'approuvee' && x.carte === cid);
  async function soumettre() {
    setErreur(null);
    try {
      setOuverte((await client.ouvrirInstance({ type_objet: 'ecriture_outil', carte: cid, plan_id: planId })).id);
    } catch (e) {
      setErreur(e);
    }
  }
  async function choisir(iid: string) {
    setErreur(null);
    if (!iid) return surAttestation(null);
    try {
      surAttestation((await client.instanceCircuit(iid)).attestation ?? null);
    } catch (e) {
      setErreur(e);
    }
  }
  return (
    <div className="pile" style={{ gap: 6 }} data-testid="circuit-ecriture">
      <p className="note">
        Circuit de validation : sans attestation de fin de circuit, l’API ne signe aucune validation. Soumettez ce plan au
        circuit de votre entreprise, puis revenez l’exécuter avec l’attestation une fois les décisions prises.
      </p>
      <div className="ligne">
        <button type="button" className="btn" onClick={() => void soumettre()}>
          Soumettre ce plan au circuit de validation
        </button>
        {approuvees.length > 0 && (
          <label>
            Exécuter avec l’attestation de{' '}
            <select value="" onChange={(e) => void choisir(e.target.value)}>
              <option value="">{attestation ? 'attestation choisie' : 'choisir une instance approuvée…'}</option>
              {approuvees.map((x) => (
                <option key={x.instance} value={x.instance}>
                  référence {x.reference}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {attestation && <p className="encadre ok">Attestation choisie : <span className="num">{attestation.slice(0, 8)}</span> (usage unique).</p>}
      {ouverte && (
        <p className="encadre ok" role="status">
          Plan soumis : <a href={`#/circuits/${ouverte}`}>suivre l’instance</a>.
        </p>
      )}
      {!!erreur && <Erreur erreur={erreur} titre="Rien n’a été soumis." />}
    </div>
  );
}
